import InputAdornment from "@mui/material/InputAdornment";
import React, { FunctionComponent } from "react";
import { tokens } from "../layout/theme";
import { TrafficLightParametersModel } from "../../models/traffic-light/traffic-light.model";
import { useField } from "formik";
import {
  FormControl,
  SelectChangeEvent,
  TextField,
  useTheme,
  Select,
  MenuItem,
} from "@mui/material";

import {
  TrafficDataTooltip,
  MikroDataTooltip,
  DipSlideTrafficDataTooltip,
} from "../../utils/form-field-icons";

/**
 * Interface defining the props for the FormTextField component.
 * @property {string} name - The name of the form field, used for formik integration
 * @property {string} label - The label text to display above the input
 * @property {string} [type] - The type of input (e.g., 'text', 'number', 'password')
 * @property {boolean} [required] - Whether the field is required
 * @property {boolean} [disabled] - Whether the field is disabled
 * @property {unknown} [value] - The current value of the field
 * @property {string} [endAdornmentText] - Text to display at the end of the input
 * @property {boolean} [multiline] - Whether the input should be multiline
 * @property {number|string} [minRows] - Minimum number of rows for multiline input
 * @property {TrafficLightParametersModel|null} [trafficData] - Traffic light data for validation display
 * @property {any} [mikroData] - Micro data for validation display
 * @property {any} [key] - React key for the component
 * @property {boolean} [showComparisonDropdown] - Whether to show the comparison operator dropdown
 * @property {string} [comparisonValue] - The selected comparison operator (<, >, =)
 * @property {(value: string) => void} [onComparisonChange] - Callback for comparison operator changes
 * @property {(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>) => void} [onChange] - Callback for input changes
 */
interface FormTextFieldProps {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  value?: unknown;
  endAdornmentText?: string;
  multiline?: boolean;
  minRows?: number | string;
  trafficData?: TrafficLightParametersModel | null;
  dipSlideTrafficData?: any;
  mikroData?: any;
  key?: any;
  showComparisonDropdown?: boolean;
  comparisonValue?: string;
  onComparisonChange?: (value: string) => void;
  onChange?: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<any>
  ) => void;
}

/**
 * A reusable form text field component that integrates with Formik and Material-UI.
 * 
 * This component provides a styled text input field with support for:
 * - Form validation and error handling
 * - Custom adornments and tooltips
 * - Traffic light and micro data indicators
 * - Multiline input support
 * - Theme-based styling
 * 
 * @component
 * @param {FormTextFieldProps} props - The props passed to the component
 * @param {string} props.name - The name of the form field
 * @param {string} props.label - The label text
 * @param {string} [props.type='text'] - The input type
 * @param {boolean} [props.required=false] - Whether the field is required
 * @param {boolean} [props.disabled=false] - Whether the field is disabled
 * @param {string} [props.endAdornmentText=''] - Text to display at the end
 * @param {boolean} [props.multiline=false] - Whether to use multiline input
 * @param {number|string} [props.minRows='2'] - Minimum rows for multiline
 * @param {TrafficLightParametersModel|null} [props.trafficData] - Traffic light data
 * @param {any} [props.mikroData] - Micro data for validation
 * @param {boolean} [props.showComparisonDropdown=false] - Whether to show comparison dropdown
 * @param {string} [props.comparisonValue='='] - Selected comparison operator
 * @param {Function} [props.onComparisonChange] - Comparison change handler
 * @param {Function} [props.onChange] - Change event handler
 * 
 * @example
 * ```tsx
 * <FormTextField
 *   name="email"
 *   label="Email Address"
 *   type="email"
 *   required
 *   trafficData={trafficLightData}
 * />
 * ```
 * 
 * @remarks
 * - Integrates with Formik for form state management
 * - Uses Material-UI components for styling
 * - Supports custom validation indicators
 * - Handles number input wheel events
 * - Provides consistent styling with the application theme
 * 
 * @returns {JSX.Element} A React component that renders a form text field
 */
const FormTextField: FunctionComponent<FormTextFieldProps> = ({
  name,
  type,
  value,
  required = false,
  disabled = false,
  endAdornmentText = "",
  multiline = false,
  minRows = "2",
  trafficData,
  dipSlideTrafficData,
  mikroData,
  showComparisonDropdown = false,
  comparisonValue = "=",
  onComparisonChange,
  onChange,
  ...props
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [field, meta] = useField(name);
  const ref = React.useRef();

  if (ref.current && type === 'number') {
    // @ts-ignore
    ref.current.addEventListener("wheel", (e: any) => {
      e.preventDefault();
      return false;
    });
  }

  const configTextField = {
    ...field,
    ...props,
    type: type,
    value: value,
    required: required,
    disabled: disabled,
    multiline: multiline,
    minRows: minRows,
    onChange: onChange,
    error: !!meta.touched && !!meta.error,
    helperText: meta.touched && meta.error ? meta.error : undefined,
  };

  const comparisonOptions = [
    { value: "<", label: "<" },
    { value: ">", label: ">" },
    { value: "=", label: "=" },
  ];

  const handleComparisonChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    if (onComparisonChange) {
      onComparisonChange(newValue);
    }
  };

  return (
    <>
      <FormControl fullWidth variant="outlined">
        <TextField
          inputRef={ref}
          variant="outlined"
          fullWidth
          {...configTextField}
          FormHelperTextProps={{ style: { minHeight: "20px" } }}
          sx={{
            borderRadius: 2,
            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.blueAccent[600],
            },
            "& .MuiFormLabel-root.Mui-focused": {
              color:
                theme.palette.mode === "dark" ? "white" : colors.primary[300],
            },
            "& .MuiFormHelperText-root.Mui-error": {
              marginLeft: 0,
            },
            "& .MuiOutlinedInput-root": {
              color: colors.primary[100],
            },
          }}
          inputProps={{ lang: "de" }}
          InputProps={{
            startAdornment: showComparisonDropdown ? (
              <InputAdornment position="start">
                <Select
                  value={comparisonValue}
                  onChange={handleComparisonChange}
                  size="small"
                  disabled={disabled}
                  variant="outlined"
                  sx={{
                    minWidth: "60px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& .MuiSelect-select": {
                      color: colors.primary[100],
                      textAlign: "center",
                      fontWeight: "bold",
                      padding: "4px 8px",
                      fontSize: "14px",
                    },
                    "& .MuiSelect-icon": {
                      display: "none", // Hide the dropdown arrow for cleaner look
                    },
                  }}
                >
                  {comparisonOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </InputAdornment>
            ) : null,
            endAdornment:
              endAdornmentText || trafficData || mikroData || dipSlideTrafficData ? (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {trafficData && (
                    <TrafficDataTooltip
                      value={value}
                      trafficData={trafficData}
                      colors={colors}
                    />
                  )}
                  {dipSlideTrafficData && (
                    <DipSlideTrafficDataTooltip
                      value={value}
                      trafficData={dipSlideTrafficData}
                      colors={colors}
                    />
                  )}
                  {mikroData && (
                    <MikroDataTooltip
                      value={value}
                      trafficData={mikroData}
                      colors={colors}
                      comparisonOperator={comparisonValue}
                    />
                  )}
                  {endAdornmentText && (
                    <InputAdornment
                      sx={{
                        backgroundColor: "#6FB6FF",
                        color: "white",
                        fontWeight: "bold",
                        borderRadius: "4px",
                        padding: "12px 16px",
                      }}
                      position="end"
                    >
                      {endAdornmentText}
                    </InputAdornment>
                  )}
                </div>
              ) : null,
          }}
        />
      </FormControl>
    </>
  );
};

export default FormTextField;
